<template>
  <div class="game-menu">
    <!-- First Row - Game Categories -->
    <section class="game-categories">
      <div class="categories-grid">
        <div
          v-for="category in resolvedGameCategories"
          :key="category.keyName"
          class="category-card"
          :class="{
            active: selectedKeyName === category.keyName
          }"
          @click="selectCategory(category.keyName)"
        >
          <div class="category-background">
            <img
              :src="category.image"
              :alt="category.label"
              class="category-image"
            />
            <div class="category-overlay"></div>
          </div>
        </div>
      </div>
    </section>

    <!-- Second Row - Game Types -->
    <section class="game-types">
      <div class="game-types-container" ref="gameTypesContainer">
        <div
          v-for="gameType in gameTypes"
          :key="gameType.keyName"
          class="game-type-item"
          :class="{
            active: selectedKeyName === gameType.keyName
          }"
          @click="selectGameType(gameType.keyName)"
        >
          <div class="game-type-icon">
            {{ gameType.icon }}
          </div>
          <div class="game-type-label">{{ gameType.label }}</div>
        </div>
      </div>
    </section>
  </div>
</template>

<script>
export default {
  name: 'GameMenu',
  props: {
    selectedKeyName: {
      type: String,
      default: null
    },
    gameCategories: {
      type: Array,
      default: null
    },
    gameTypes: {
      type: Array,
      default: () => [
        { keyName: 'slots', icon: '🎰', label: 'สล็อต' },
        { keyName: 'baccarat', icon: '🃏', label: 'บาคาร่า' },
        { keyName: 'fishing', icon: '🐠', label: 'ยิงปลา' },
        { keyName: 'lottery', icon: '🎲', label: 'หวย' },
        { keyName: 'sport', icon: '⚽', label: 'กีฬา' }
      ]
    }
  },
  data() {
    return {}
  },
  computed: {
    // Built from the reactive $theme so category images update when the
    // admin-managed theme arrives from the API after mount.
    resolvedGameCategories() {
      if (this.gameCategories) return this.gameCategories
      const { gameCategoryImages = {} } = this.$theme || {}
      return [
        {
          keyName: 'new-games',
          label: 'เกมส์ใหม่',
          image: gameCategoryImages.newGames
        },
        {
          keyName: 'hot-games',
          label: 'เกมส์แตกตอนนี้',
          image: gameCategoryImages.hotGames
        },
        {
          keyName: 'popular-games',
          label: 'เกมส์คนเล่นเยอะ',
          image: gameCategoryImages.popularGames
        }
      ]
    }
  },
  mounted() {
    // Component mounted - native scrolling will handle everything
  },
  methods: {
    selectCategory(keyName) {
      this.$emit('selection-change', { keyName })
    },
    selectGameType(keyName) {
      this.$emit('selection-change', { keyName })
    }
  }
}
</script>

<style lang="scss" scoped>
.game-menu {
  width: 100%;
}

// First Row - Game Categories
.game-categories {
  margin-bottom: 5px;
}

.categories-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 15px;
  padding: 0 20px;

  @media (max-width: 768px) {
    gap: 10px;
    padding: 0 15px;
  }
}

.category-card {
  position: relative;
  aspect-ratio: 3/3.2;
  border-radius: 15px;
  overflow: hidden;
  cursor: pointer;
  border: 1px dashed rgba(var(--theme-primary-rgb), 0.5);

  @media (hover: hover) {
    &:hover {
      transform: translateY(-3px);
      box-shadow: 0 6px 20px rgba(0, 0, 0, 0.4);

      .category-image {
        transform: scale(1.05);
        filter: brightness(1.1);
      }
    }
  }

  @media (max-width: 768px) {
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.3);
  }

  &.active {
    position: relative;
    transform: translateY(-3px);
    z-index: 2;
    border: 2px solid var(--theme-primary);
    border-radius: 20px;
    overflow: hidden;
    animation: none;
    box-shadow: 0 0 20px rgba(var(--theme-primary-rgb), 0.6),
      0 4px 16px rgba(0, 0, 0, 0.4);

    @media (max-width: 768px) {
      box-shadow: 0 0 10px rgba(var(--theme-primary-rgb), 0.4),
        0 2px 8px rgba(0, 0, 0, 0.3);
      transform: translateY(-3px);
    }

    .category-label {
      color: var(--theme-primary);
      text-shadow: 0 0 8px rgba(var(--theme-primary-rgb), 0.8);
      font-weight: 400;
      letter-spacing: 0.5px;
    }

    .category-image {
      filter: brightness(1.1);
      transform: scale(1.05);
    }
  }
}

.category-background {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: 1;
}

.category-image {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.category-overlay {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.15);
  transition: all 0.05s ease;
}

.category-content {
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  padding: 15px;
  z-index: 3;
  text-align: center;
  background: rgba(0, 0, 0, 0.8);
}

.category-label {
  color: #fff;
  font-size: clamp(2.2rem, 2vw, 2.5rem);
  font-weight: 300;
  text-shadow: 0 2px 10px rgba(0, 0, 0, 0.8);
  transition: all 0.3s ease;
  margin: 0;
}

.click-icon {
  position: absolute;
  top: 12px;
  right: 12px;
  font-size: 1.2rem;
  opacity: 0.6;
  z-index: 5;
  transition: all 0.3s ease;
  pointer-events: none;
}

.category-card:hover .click-icon {
  opacity: 1;
  transform: scale(1.1);
}

.category-card.active .click-icon {
  opacity: 0;
}

// Second Row - Game Types
.game-types {
  padding: 0px;

  @media (max-width: 768px) {
    padding: 0px;
  }
}

.game-types-container {
  display: flex;
  gap: 12px;
  overflow-x: auto;
  overflow-y: hidden;
  scrollbar-width: none;
  -ms-overflow-style: none;
  padding: 15px 20px;
  align-items: center;
  min-height: 80px;

  @media (max-width: 768px) {
    gap: 10px;
    padding: 12px 15px;
    min-height: 70px;
  }

  @media (max-width: 480px) {
    gap: 8px;
    padding: 10px 12px;
  }
}

.game-type-item {
  flex: 0 0 auto;
  display: flex;
  flex-direction: row;
  align-items: center;
  padding: 12px 18px;
  min-width: auto;
  border-radius: 25px; // Pill-shaped design
  background: rgba(255, 255, 255, 0.08);
  border: 2px solid rgba(255, 255, 255, 0.2);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
  cursor: pointer;
  transition: all 0.3s ease;
  position: relative;
  overflow: hidden;
  gap: 10px;
  transform: translate3d(0, 0, 0);

  @media (hover: hover) {
    &:hover {
      transform: translateY(-4px);
      background: rgba(var(--theme-primary-rgb), 0.2);
      border-color: rgba(var(--theme-primary-rgb), 0.5);
      box-shadow: 0 6px 20px rgba(0, 0, 0, 0.4),
        0 0 15px rgba(var(--theme-primary-rgb), 0.4);

      .game-type-icon {
        transform: scale(1.1);
      }

      .game-type-label {
        text-shadow: 0 0 6px rgba(var(--theme-primary-rgb), 0.4);
      }
    }
  }

  @media (max-width: 768px) {
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);
  }

  &.active {
    background: var(--theme-primary);
    border-color: rgba(var(--theme-primary-rgb), 0.3);
    box-shadow: 0 6px 10px rgba(var(--theme-primary-rgb), 0.4);

    .game-type-icon {
      @media (hover: hover) {
        animation: icon-pulse 2s ease-in-out infinite;
      }
    }
  }

  @media (max-width: 768px) {
    padding: 10px 15px;
    gap: 8px;
    border-radius: 22px;

    &:hover {
      transform: translateY(-3px);
    }
  }

  @media (max-width: 480px) {
    padding: 8px 12px;
    gap: 6px;
    border-radius: 20px;
  }
}

.game-type-icon {
  font-size: clamp(1.4rem, 3vw, 1.6rem);
  margin-bottom: 0;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  position: relative;
  z-index: 3;
}

.game-type-label {
  color: #fff;
  font-size: clamp(0.95rem, 1.5vw, 1.1rem);
  font-weight: 600;
  text-align: center;
  white-space: nowrap;
  transition: all 0.3s ease;
  line-height: 1.3;
  letter-spacing: 0.3px;
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.5);
  position: relative;
  z-index: 3;
}

// Lightweight Animations
@keyframes pulse-aura {
  0%,
  100% {
    opacity: 0.6;
  }
  50% {
    opacity: 1;
  }
}

@keyframes pulse-glow {
  0%,
  100% {
    opacity: 0.6;
  }
  50% {
    opacity: 1;
  }
}

@keyframes icon-pulse {
  0%,
  100% {
    opacity: 1;
  }
  50% {
    opacity: 0.8;
  }
}

@media (max-width: 768px) {
  .categories-grid {
    gap: 8px;
  }

  .category-content {
    padding: 10px;
  }
}

@media (max-width: 480px) {
  .categories-grid {
    padding: 0px;
  }

  .category-label {
    font-size: 1.1rem;
  }
}
</style>
